import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type Author,
  type Editor,
  type Person,
  type Reader,
  type PersonRoleSnapshot,
  type PersonCatalogStaffRole,
  type PersonCatalogSaveRequest,
  type ReaderPriceInput,
} from '@/api/client';
import { Modal } from '@/components/ui/Modal/Modal';
import { Input } from '@/components/ui/Input/Input';
import { EmailInput } from '@/components/ui/EmailInput/EmailInput';
import { PhoneInput } from '@/components/ui/PhoneInput/PhoneInput';
import { Button } from '@/components/ui/Button/Button';
import { Switch } from '@/components/ui/Switch/Switch';
import { ComboClearButton } from '@/components/ui/ComboClearButton/ComboClearButton';
import { ContractorCombo, type ContractorValueItem } from '@/components/ContractorCombo/ContractorCombo';
import { SelectDropdown, type SelectDropdownOption } from '@/components/ui/SelectDropdown/SelectDropdown';
import { ChoiceDialog } from '@/components/ui/ChoiceDialog/ChoiceDialog';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/useAuth';
import { omitPriceKeys } from '@/lib/omitPriceKeys';
import { ruPhoneToE164 } from '@/utils/phoneRu';
import { formatPriceInputDisplay, sortReaderCatalogPrices } from '@/utils/formatNumber';
import {
  ReaderCatalogPricePositionsEditor,
  MAX_READER_CATALOG_PRICE_POSITIONS,
  type ReaderCatalogPriceDraftRow,
} from '@/components/ReaderCatalogPricePositionsEditor/ReaderCatalogPricePositionsEditor';
import { ReaderCooperationSelect } from '@/components/ReaderCooperationSelect/ReaderCooperationSelect';
import { READERS_TABLE_QUERY_KEY } from '@/lib/readersCatalogQueryCache';
import {
  normalizeReaderCooperationState,
  READER_COOPERATION_DEFAULT,
  type ReaderCooperationStateValue,
} from '@/utils/readerCooperationStates';

/** Предустановка галочек ролей (например из модалки «Добавить проект») */
export type CreatePersonRolePreset = 'reader' | 'author' | 'editor';

/** Ответы API по созданным ролям — для подстановки в поля проекта */
export interface CreatePersonCreatedPayload {
  person: Person;
  authors: Author[];
  readers: Reader[];
  editor: Editor | null;
}

export interface CreatePersonModalProps {
  open: boolean;
  onClose: () => void;
  /** Если задано при открытии — включаются соответствующие роли (редактор: сотрудник + роль «Редактор») */
  rolePreset?: CreatePersonRolePreset | null;
  /** Подставить редакцию для предустановки «редактор» (например из поля «Редакция» проекта) */
  editionIdPreset?: string | null;
  /** При открытии — сразу форма управления каталогом для этой персоны (id persons) */
  personIdPreset?: string | null;
  /** После успешного сохранения — добавить персону в открытые поля проекта */
  onCreated?: (payload: CreatePersonCreatedPayload) => void;
  /** Без обёртки Modal — для встраивания в CatalogManageModal */
  embedded?: boolean;
  /** Из режима редактирования — начать создание новой персоны */
  onRequestCreate?: () => void;
  /** Префилл имени/фамилии при создании (из поиска комбо) */
  nameSeed?: string | null;
}

type StaffRole = 'producer' | 'soundengineer' | 'techeditor' | 'corrector' | 'editor' | '';
type ReaderLinkDraft = { link: string; link_name: string };

type WizardStep = 'name' | 'form';

type RoleBaseline = {
  isReader: boolean;
  isAuthor: boolean;
  isEmployee: boolean;
  isUser: boolean;
  staffRole: StaffRole;
  editionId: string;
};

type DeletePersonDialogState =
  | { mode: 'reader-only'; personId: string; name: string }
  | { mode: 'multi-role'; personId: string; name: string; otherRoles: string[] }
  | { mode: 'generic'; personId: string; name: string; rolesLine: string };

/** Совпадает с лимитом на сервере (readers routes) */
const MAX_READER_DEMO_LINKS = 3;

const STAFF_ROLE_OPTIONS: SelectDropdownOption[] = [
  { value: 'producer', label: 'Продюсер' },
  { value: 'soundengineer', label: 'Звукорежиссёр' },
  { value: 'techeditor', label: 'Техредактор' },
  { value: 'corrector', label: 'Корректор' },
  { value: 'editor', label: 'Редактор' },
];

const STAFF_ROLE_LABELS: Record<Exclude<StaffRole, ''>, string> = {
  producer: 'продюсер',
  soundengineer: 'звукорежиссёр',
  techeditor: 'техредактор',
  corrector: 'корректор',
  editor: 'редактор',
};

const EMPTY_STAFF: StaffRole = '';

function splitPersonNameSeed(raw: string | null | undefined): { first: string; last: string } {
  const parts = (raw ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { first: '', last: '' };
  if (parts.length === 1) return { first: parts[0]!, last: '' };
  return { first: parts[0]!, last: parts.slice(1).join(' ') };
}

function catalogStaffToStaffRole(role: PersonCatalogStaffRole): StaffRole {
  return role;
}

function parsePrice(raw: string): number | null {
  const t = raw.trim().replace(',', '.');
  if (!t) return null;
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : null;
}

function readerLinksPayload(links: ReaderLinkDraft[]) {
  return links
    .slice(0, MAX_READER_DEMO_LINKS)
    .map((item, index) => ({
      link: item.link.trim(),
      link_name: item.link_name.trim() || 'Демо',
      display_order: index,
    }))
    .filter((item) => item.link);
}

function validateReaderPricesForCatalog(
  rows: ReaderCatalogPriceDraftRow[]
): { ok: true; prices: ReaderPriceInput[] } | { ok: false; error: string } {
  const out: ReaderPriceInput[] = [];
  let idx = 0;
  for (const row of rows) {
    const name = row.price_name.trim();
    const t = row.price.trim();
    if (!name && !t) continue;
    if (name && !t) return { ok: false, error: 'Укажите сумму для каждой названной позиции' };
    const n = parsePrice(row.price);
    if (n == null) return { ok: false, error: 'Введите корректную сумму для позиций стоимости' };
    out.push({
      id: row.id,
      price_name: name ? name : undefined,
      price: n,
      display_order: idx,
    });
    idx += 1;
  }
  if (out.length > MAX_READER_CATALOG_PRICE_POSITIONS) {
    return { ok: false, error: `Не больше ${MAX_READER_CATALOG_PRICE_POSITIONS} позиций цены` };
  }
  return { ok: true, prices: out };
}

function snapshotRolesSummaryLine(s: PersonRoleSnapshot): string {
  const parts: string[] = [];
  if (s.reader) parts.push('чтец');
  if (s.has_author) parts.push('автор');
  if (s.staff) parts.push(STAFF_ROLE_LABELS[s.staff.role] ?? s.staff.role);
  if (s.user) parts.push('пользователь');
  return parts.length ? parts.join(', ') : 'без каталожных ролей';
}

function buildRemovalLabels(baseline: RoleBaseline, cur: RoleBaseline): string[] {
  const labels: string[] = [];
  if (baseline.isReader && !cur.isReader) labels.push('роль чтеца и демо-ссылки');
  if (baseline.isAuthor && !cur.isAuthor) labels.push('автор в каталоге');
  if (baseline.isUser && !cur.isUser) labels.push('учётная запись пользователя');
  if (baseline.isEmployee && !cur.isEmployee && baseline.staffRole) {
    labels.push(`сотрудник (${STAFF_ROLE_LABELS[baseline.staffRole]})`);
  } else if (
    baseline.isEmployee &&
    cur.isEmployee &&
    baseline.staffRole &&
    cur.staffRole &&
    baseline.staffRole !== cur.staffRole
  ) {
    labels.push(`роль «${STAFF_ROLE_LABELS[baseline.staffRole]}»`);
  }
  return labels;
}

function readBaseline(params: {
  isReader: boolean;
  isAuthor: boolean;
  isEmployee: boolean;
  isUser: boolean;
  staffRole: StaffRole;
  editionId: string;
}): RoleBaseline {
  return {
    isReader: params.isReader,
    isAuthor: params.isAuthor,
    isEmployee: params.isEmployee,
    isUser: params.isUser,
    staffRole: params.staffRole,
    editionId: params.editionId,
  };
}

export function CreatePersonModal({
  open,
  onClose,
  rolePreset = null,
  editionIdPreset = null,
  personIdPreset = null,
  onCreated,
  embedded = false,
  onRequestCreate,
  nameSeed = null,
}: CreatePersonModalProps) {
  const { showSuccess, showError } = useToast();
  const { capabilities } = useAuth();
  const showReaderPrices = capabilities.prices;
  const queryClient = useQueryClient();

  const [wizardStep, setWizardStep] = useState<WizardStep>('name');
  const [checking, setChecking] = useState(false);
  const [existingPersonId, setExistingPersonId] = useState<string | null>(null);
  const [roleBaseline, setRoleBaseline] = useState<RoleBaseline | null>(null);
  const [snapshotForDelete, setSnapshotForDelete] = useState<PersonRoleSnapshot | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const [isReader, setIsReader] = useState(false);
  const [isAuthor, setIsAuthor] = useState(false);
  const [isEmployee, setIsEmployee] = useState(false);
  const [isUser, setIsUser] = useState(false);

  const [readerPrices, setReaderPrices] = useState<ReaderCatalogPriceDraftRow[]>([]);
  const [readerLinks, setReaderLinks] = useState<ReaderLinkDraft[]>([]);
  const [readerStudio, setReaderStudio] = useState<ContractorValueItem | null>(null);
  const [readerLegalEntity, setReaderLegalEntity] = useState<ContractorValueItem | null>(null);
  const [readerCooperation, setReaderCooperation] =
    useState<ReaderCooperationStateValue>(READER_COOPERATION_DEFAULT);

  const [staffRole, setStaffRole] = useState<StaffRole>(EMPTY_STAFF);
  const [editionId, setEditionId] = useState('');

  const [userRole, setUserRole] = useState('');
  const [userAccess, setUserAccess] = useState('');
  const [userIsActive, setUserIsActive] = useState(true);
  const [staffDismissed, setStaffDismissed] = useState(false);

  const [saving, setSaving] = useState(false);

  const [removeRolesDialog, setRemoveRolesDialog] = useState<{ labels: string[] } | null>(null);
  const [deletePersonDialog, setDeletePersonDialog] = useState<DeletePersonDialogState | null>(null);
  const [deletePersonSubmitting, setDeletePersonSubmitting] = useState(false);
  const [personBootstrapLoading, setPersonBootstrapLoading] = useState(false);

  const readerRoleLocked = rolePreset === 'reader';
  const authorRoleLocked = rolePreset === 'author';
  const editorRoleLocked = rolePreset === 'editor';

  const resetForm = useCallback(() => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setIsReader(false);
    setIsAuthor(false);
    setIsEmployee(false);
    setIsUser(false);
    setReaderPrices([]);
    setReaderLinks([]);
    setReaderStudio(null); setReaderLegalEntity(null);
    setReaderCooperation(READER_COOPERATION_DEFAULT);
    setStaffRole(EMPTY_STAFF);
    setEditionId('');
    setUserRole('');
    setUserAccess('');
    setUserIsActive(true);
    setStaffDismissed(false);
  }, []);

  useEffect(() => {
    if (!open) {
      setWizardStep('name');
      setExistingPersonId(null);
      setRoleBaseline(null);
      setSnapshotForDelete(null);
      setChecking(false);
      setRemoveRolesDialog(null);
      setDeletePersonDialog(null);
      setDeletePersonSubmitting(false);
      setPersonBootstrapLoading(false);
      return;
    }
    setWizardStep('name');
    setExistingPersonId(null);
    setRoleBaseline(null);
    setSnapshotForDelete(null);
    const seeded = splitPersonNameSeed(nameSeed);
    setFirstName(seeded.first);
    setLastName(seeded.last);
    setEmail('');
    setPhone('');
    setReaderPrices([]);
    setReaderLinks([]);
    setReaderStudio(null); setReaderLegalEntity(null);
    setReaderCooperation(READER_COOPERATION_DEFAULT);
    setUserRole('');
    setUserAccess('');
    setSaving(false);
    setChecking(false);
    setRemoveRolesDialog(null);
    setDeletePersonDialog(null);
    setIsReader(false);
    setIsAuthor(false);
    setIsEmployee(false);
    setIsUser(false);
    setStaffRole(EMPTY_STAFF);
    setEditionId('');

    if (rolePreset === 'reader') {
      setIsReader(true);
    } else if (rolePreset === 'author') {
      setIsAuthor(true);
    } else if (rolePreset === 'editor') {
      setIsEmployee(true);
      setStaffRole('editor');
      const ep = editionIdPreset?.trim();
      if (ep) setEditionId(ep);
    }
  }, [open, rolePreset, editionIdPreset, nameSeed]);

  useEffect(() => {
    if (!isEmployee) {
      setStaffRole(EMPTY_STAFF);
      setEditionId('');
      setStaffDismissed(false);
    }
  }, [isEmployee]);

  useEffect(() => {
    if (staffRole !== 'editor') setEditionId('');
  }, [staffRole]);

  useEffect(() => {
    if (!isReader) {
      setReaderStudio(null); setReaderLegalEntity(null);
      setReaderLinks([]);
      setReaderPrices([]);
      setReaderCooperation(READER_COOPERATION_DEFAULT);
    }
  }, [isReader]);

  const editionsQuery = useQuery({
    queryKey: ['editions', 'createPersonModal'],
    queryFn: async () => {
      const res = await api.editions.list(undefined, undefined, { limit: 200, offset: 0 });
      return res.items;
    },
    enabled: open && wizardStep === 'form' && isEmployee && staffRole === 'editor',
    staleTime: 60_000,
  });

  const editionOptions: SelectDropdownOption[] = useMemo(() => {
    const items = editionsQuery.data ?? [];
    return items.map((e) => ({ value: e.id, label: e.name }));
  }, [editionsQuery.data]);

  const applyPresetLocks = useCallback(
    (base: {
      isReader: boolean;
      isAuthor: boolean;
      isEmployee: boolean;
      isUser: boolean;
      staffRole: StaffRole;
      editionId: string;
    }) => {
      let next = { ...base };
      if (readerRoleLocked) next = { ...next, isReader: true };
      if (authorRoleLocked) next = { ...next, isAuthor: true };
      if (editorRoleLocked) {
        next = { ...next, isEmployee: true, staffRole: 'editor' as StaffRole };
        const ep = editionIdPreset?.trim();
        if (ep) next = { ...next, editionId: ep };
      }
      return next;
    },
    [readerRoleLocked, authorRoleLocked, editorRoleLocked, editionIdPreset]
  );

  const handleClose = useCallback(() => {
    if (!saving && !deletePersonSubmitting) onClose();
  }, [onClose, saving, deletePersonSubmitting]);

  /** Полная подстановка формы и baseline по снимку с сервера (существующая персона). */
  const loadExistingPersonIntoForm = useCallback(
    async (personId: string) => {
      const snap = await api.persons.roleSnapshot(personId);
      setSnapshotForDelete(snap);
      setExistingPersonId(personId);

      setFirstName(snap.person.first_name);
      setLastName(snap.person.last_name);
      setEmail(snap.person.email ?? '');
      setPhone(snap.person.phone ?? '');

      const hasReader = !!snap.reader;
      const nextIsReader = hasReader;
      let nextReaderPrices: ReaderCatalogPriceDraftRow[] = [];
      let nextLinks: ReaderLinkDraft[] = [];
      let nextStudio: ContractorValueItem | null = null;
      let nextLegalEntity: ContractorValueItem | null = null;
      if (snap.reader) {
        nextReaderPrices = sortReaderCatalogPrices(snap.reader.reader_prices).map((p) => ({
          id: p.id,
          price_name: p.price_name?.trim() ?? '',
          price: p.price != null ? formatPriceInputDisplay(p.price) : '',
        }));
        nextLinks = (snap.reader.links ?? []).map((l) => ({
          link: l.link,
          link_name: l.link_name ?? '',
        }));
        const st = snap.reader.studio;
        nextStudio = st?.id ? { id: st.id, name: st.name } : null;
        const le = snap.reader.legal_entity;
        nextLegalEntity = le?.id ? { id: le.id, name: le.name } : null;
      }
      setIsReader(nextIsReader);
      setReaderPrices(nextReaderPrices);
      setReaderLinks(nextLinks);
      setReaderStudio(nextStudio);
      setReaderLegalEntity(nextLegalEntity);
      setReaderCooperation(
        snap.reader ? normalizeReaderCooperationState(snap.reader.cooperation_state) : READER_COOPERATION_DEFAULT
      );

      setIsAuthor(snap.has_author);

      setIsUser(!!snap.user);
      setUserRole(snap.user?.role ?? '');
      setUserAccess(snap.user?.access ?? '');
      setUserIsActive(snap.user?.is_active !== false);

      const hasStaff = !!snap.staff;
      let nextStaff: StaffRole = EMPTY_STAFF;
      let nextEdition = '';
      if (snap.staff) {
        nextStaff = catalogStaffToStaffRole(snap.staff.role);
        if (snap.staff.role === 'editor' && snap.staff.edition_id) nextEdition = snap.staff.edition_id;
      }
      setIsEmployee(hasStaff);
      setStaffRole(hasStaff ? nextStaff : EMPTY_STAFF);
      setEditionId(nextEdition);
      setStaffDismissed(snap.staff?.dismissed === true);

      const locked = applyPresetLocks({
        isReader: nextIsReader,
        isAuthor: snap.has_author,
        isEmployee: hasStaff,
        isUser: !!snap.user,
        staffRole: hasStaff ? nextStaff : EMPTY_STAFF,
        editionId: nextEdition,
      });
      setIsReader(locked.isReader);
      setIsAuthor(locked.isAuthor);
      setIsEmployee(locked.isEmployee);
      setIsUser(locked.isUser);
      setStaffRole(locked.staffRole);
      setEditionId(locked.editionId);

      setRoleBaseline(
        readBaseline({
          isReader: locked.isReader,
          isAuthor: locked.isAuthor,
          isEmployee: locked.isEmployee,
          isUser: locked.isUser,
          staffRole: locked.staffRole,
          editionId: locked.editionId,
        })
      );
    },
    [applyPresetLocks]
  );

  useEffect(() => {
    if (!open) return;
    const pid = typeof personIdPreset === 'string' ? personIdPreset.trim() : '';
    if (!pid) return;

    let cancelled = false;
    setWizardStep('form');
    setPersonBootstrapLoading(true);
    void loadExistingPersonIntoForm(pid)
      .then(() => {
        if (!cancelled) setPersonBootstrapLoading(false);
      })
      .catch((e) => {
        if (!cancelled) {
          showError(e instanceof Error ? e.message : 'Не удалось загрузить данные персоны');
          setPersonBootstrapLoading(false);
          setWizardStep('name');
          setExistingPersonId(null);
          setRoleBaseline(null);
          setSnapshotForDelete(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, personIdPreset, loadExistingPersonIntoForm, showError]);

  const invalidatePersonRelated = useCallback(async () => {
    void queryClient.invalidateQueries({ queryKey: ['authors'] });
    void queryClient.invalidateQueries({ queryKey: ['readers'] });
    void queryClient.invalidateQueries({ queryKey: ['persons'] });
    void queryClient.invalidateQueries({ queryKey: READERS_TABLE_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: ['editors'] });
    void queryClient.invalidateQueries({ queryKey: ['users'] });
    void queryClient.invalidateQueries({ queryKey: ['producers'] });
    void queryClient.invalidateQueries({ queryKey: ['soundengineers'] });
    void queryClient.invalidateQueries({ queryKey: ['techeditors'] });
    void queryClient.invalidateQueries({ queryKey: ['correctors'] });
  }, [queryClient]);

  const handleCheckPerson = async () => {
    const fn = firstName.trim();
    const ln = lastName.trim();
    if (!fn || !ln) {
      showError('Укажите имя и фамилию');
      return;
    }
    setChecking(true);
    try {
      const res = await api.persons.lookup({ first_name: fn, last_name: ln });
      if (!res.found || !res.person) {
        showSuccess('Персона ещё не создана');
        setExistingPersonId(null);
        setSnapshotForDelete(null);
        setWizardStep('form');
        const preset = applyPresetLocks({
          isReader,
          isAuthor,
          isEmployee,
          isUser,
          staffRole,
          editionId,
        });
        setIsReader(preset.isReader);
        setIsAuthor(preset.isAuthor);
        setIsEmployee(preset.isEmployee);
        setIsUser(preset.isUser);
        setStaffRole(preset.staffRole);
        setEditionId(preset.editionId);
        setRoleBaseline(
          readBaseline({
            isReader: preset.isReader,
            isAuthor: preset.isAuthor,
            isEmployee: preset.isEmployee,
            isUser: preset.isUser,
            staffRole: preset.staffRole,
            editionId: preset.editionId,
          })
        );
        return;
      }

      showSuccess('Персона уже создана');
      await loadExistingPersonIntoForm(res.person.id);
      setWizardStep('form');
      return;
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Не удалось проверить');
    } finally {
      setChecking(false);
    }
  };

  const buildCatalogSaveBody = (readerPricesPayload: ReaderPriceInput[]): PersonCatalogSaveRequest => {
    const fn = firstName.trim();
    const ln = lastName.trim();
    const links = readerLinksPayload(readerLinks);
    const body: PersonCatalogSaveRequest = {
      person_id: existingPersonId,
      first_name: fn,
      last_name: ln,
      email: email.trim() ? email.trim() : null,
      phone: (ruPhoneToE164(phone) ?? null) as string | null,
      is_reader: isReader,
      cooperation_state: isReader ? readerCooperation : null,
      reader_prices: showReaderPrices ? readerPricesPayload : [],
      reader_links: links.map((item, index) => ({
        link: item.link,
        link_name: item.link_name,
        display_order: item.display_order ?? index,
      })),
      studio_id: readerStudio?.id ?? null,
      legal_entity_id: readerLegalEntity?.id ?? null,
      is_author: isAuthor,
      is_employee: isEmployee,
      staff_role: (staffRole || '') as PersonCatalogSaveRequest['staff_role'],
      edition_id: editionId.trim(),
      staff_dismissed: isEmployee ? staffDismissed : false,
      is_user: isUser,
      user_role: userRole.trim(),
      user_access: userAccess.trim(),
      is_active: userIsActive,
    };
    return body;
  };

  const performCatalogSave = async (readerPricesPayload: ReaderPriceInput[]) => {
    const body = buildCatalogSaveBody(readerPricesPayload);
    const res = await api.persons.catalogSave(showReaderPrices ? body : omitPriceKeys(body));
    showSuccess('Персона сохранена');
    await invalidatePersonRelated();
    onCreated?.({
      person: res.person,
      authors: res.created.authors,
      readers: res.created.readers,
      editor: res.created.editor ?? null,
    });
    resetForm();
    onClose();
  };

  const trySave = async () => {
    const fn = firstName.trim();
    const ln = lastName.trim();
    if (!fn || !ln) {
      showError('Укажите имя и фамилию');
      return;
    }
    if (isEmployee && !staffRole) {
      showError('Выберите должность сотрудника');
      return;
    }
    if (isEmployee && staffRole === 'editor' && !editionId) {
      showError('Выберите редакцию для редактора');
      return;
    }

    if (!roleBaseline) {
      showError('Сначала выполните проверку по ФИО');
      return;
    }

    const cur = readBaseline({
      isReader,
      isAuthor,
      isEmployee,
      isUser,
      staffRole,
      editionId,
    });
    const removals = buildRemovalLabels(roleBaseline, cur);
    if (removals.length > 0) {
      setRemoveRolesDialog({ labels: removals });
      return;
    }

    let readerPricesPayload: ReaderPriceInput[] = [];
    if (isReader && showReaderPrices) {
      const v = validateReaderPricesForCatalog(readerPrices);
      if (!v.ok) {
        showError(v.error);
        return;
      }
      readerPricesPayload = v.prices;
    }

    setSaving(true);
    try {
      await performCatalogSave(readerPricesPayload);
    } catch (e) {
      const err = e as Error & { status?: number; code?: string; person_id?: string };
      if (err.status === 409 && err.code === 'PERSON_DUPLICATE' && err.person_id) {
        showError('Персона уже существует');
        await loadExistingPersonIntoForm(err.person_id);
        setWizardStep('form');
        return;
      }
      showError(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const confirmSaveAfterRemoval = async () => {
    setRemoveRolesDialog(null);
    const fn = firstName.trim();
    const ln = lastName.trim();
    if (!fn || !ln) {
      showError('Укажите имя и фамилию');
      return;
    }
    if (isEmployee && !staffRole) {
      showError('Выберите должность сотрудника');
      return;
    }
    if (isEmployee && staffRole === 'editor' && !editionId) {
      showError('Выберите редакцию для редактора');
      return;
    }
    if (!roleBaseline) return;

    let readerPricesPayload: ReaderPriceInput[] = [];
    if (isReader && showReaderPrices) {
      const v = validateReaderPricesForCatalog(readerPrices);
      if (!v.ok) {
        showError(v.error);
        return;
      }
      readerPricesPayload = v.prices;
    }

    setSaving(true);
    try {
      await performCatalogSave(readerPricesPayload);
    } catch (e) {
      const err = e as Error & { status?: number; code?: string; person_id?: string };
      if (err.status === 409 && err.code === 'PERSON_DUPLICATE' && err.person_id) {
        showError('Персона уже существует');
        await loadExistingPersonIntoForm(err.person_id);
        setWizardStep('form');
        return;
      }
      showError(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const openDeletePersonDialog = async () => {
    if (!existingPersonId) return;
    const pid = existingPersonId;
    const name = `${firstName.trim()} ${lastName.trim()}`.trim() || 'Персона';
    if (snapshotForDelete?.reader) {
      try {
        const ctx = await api.readers.deletionContext(pid);
        if (ctx.otherRoles.length === 0) {
          setDeletePersonDialog({ mode: 'reader-only', personId: pid, name: ctx.name });
        } else {
          setDeletePersonDialog({ mode: 'multi-role', personId: pid, name: ctx.name, otherRoles: ctx.otherRoles });
        }
        return;
      } catch {
        /* fallback */
      }
    }
    const line = snapshotForDelete ? snapshotRolesSummaryLine(snapshotForDelete) : '';
    setDeletePersonDialog({
      mode: 'generic',
      personId: pid,
      name,
      rolesLine: line,
    });
  };

  const runDeletePerson = async () => {
    if (!deletePersonDialog) return;
    setDeletePersonSubmitting(true);
    try {
      await api.persons.delete(deletePersonDialog.personId);
      showSuccess('Персона удалена из базы');
      setDeletePersonDialog(null);
      await invalidatePersonRelated();
      resetForm();
      onClose();
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Не удалось удалить персону');
    } finally {
      setDeletePersonSubmitting(false);
    }
  };

  const deletePersonDescription: ReactNode =
    deletePersonDialog?.mode === 'reader-only' ? (
      <>
        <strong>{deletePersonDialog.name}</strong> есть только в каталоге чтецов. Персона будет удалена из базы целиком.
        Это действие необратимо.
      </>
    ) : deletePersonDialog?.mode === 'multi-role' ? (
      <>
        <strong>{deletePersonDialog.name}</strong> встречается ещё как {deletePersonDialog.otherRoles.join(', ')}.
        Удаление персоны сотрёт все роли и связанные данные. Это действие необратимо.
      </>
    ) : deletePersonDialog?.mode === 'generic' ? (
      <>
        Удалить <strong>{deletePersonDialog.name}</strong>
        {deletePersonDialog.rolesLine ? (
          <> из базы? Участие в системе: {deletePersonDialog.rolesLine}. </>
        ) : (
          <> из базы? </>
        )}
        Это действие необратимо.
      </>
    ) : null;

  const removeRolesDescription =
    removeRolesDialog && removeRolesDialog.labels.length > 0 ? (
      <>
        Будут удалены записи:{' '}
        <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
          {removeRolesDialog.labels.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      </>
    ) : null;

  const formBody = (
    <>
      <div className="create-person-modal__grid">
        <Input
          placeholder="Имя"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          disabled={saving}
          fullWidth
          autoComplete="given-name"
        />
        <Input
          placeholder="Фамилия"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          disabled={saving}
          fullWidth
          autoComplete="family-name"
        />
      </div>
      <div className="create-person-modal__grid">
        <EmailInput value={email} onChange={setEmail} disabled={saving} fullWidth />
        <PhoneInput value={phone} onChange={setPhone} disabled={saving} fullWidth />
      </div>

      <div>
        <div className="create-person-modal__roles" role="group" aria-label="Роли персоны">
          <span
            className="create-person-modal__role-switch"
            title={readerRoleLocked ? 'Нельзя отключить при создании чтеца' : undefined}
          >
            <Switch
              checked={isReader}
              onCheckedChange={setIsReader}
              disabled={saving || readerRoleLocked}
              label="Чтец"
            />
          </span>
          <span
            className="create-person-modal__role-switch"
            title={authorRoleLocked ? 'Нельзя отключить при создании автора' : undefined}
          >
            <Switch
              checked={isAuthor}
              onCheckedChange={setIsAuthor}
              disabled={saving || authorRoleLocked}
              label="Автор"
            />
          </span>
          <span
            className="create-person-modal__role-switch"
            title={editorRoleLocked ? 'Нельзя отключить при создании редактора' : undefined}
          >
            <Switch
              checked={isEmployee}
              onCheckedChange={setIsEmployee}
              disabled={saving || editorRoleLocked}
              label="Сотрудник"
            />
          </span>
          <Switch checked={isUser} onCheckedChange={setIsUser} disabled={saving} label="Пользователь" />
        </div>
      </div>

      {isReader ? (
        <>
          <ReaderCooperationSelect
            value={readerCooperation}
            onChange={setReaderCooperation}
            disabled={saving}
            fullWidth
          />
          <div className="create-person-modal__reader-row">
            <div
              className="create-person-modal__reader-contractor"
              role="group"
              aria-label="Студия чтеца"
              title="Опциональная привязка к студии"
            >
              <ContractorCombo
                value={readerStudio}
                onChange={setReaderStudio}
                source="studio"
                fullWidth
                placeholder="Студия"
                disabled={saving}
              />
            </div>
            <div
              className="create-person-modal__reader-contractor"
              role="group"
              aria-label="Юрлицо чтеца"
              title="Опциональная привязка к юрлицу"
            >
              <ContractorCombo
                value={readerLegalEntity}
                onChange={setReaderLegalEntity}
                source="legalEntity"
                fullWidth
                placeholder="Юрлицо"
                disabled={saving}
              />
            </div>
          </div>
          {showReaderPrices ? (
          <ReaderCatalogPricePositionsEditor
            value={readerPrices}
            onChange={setReaderPrices}
            disabled={saving}
            maxRows={MAX_READER_CATALOG_PRICE_POSITIONS}
          />
          ) : null}
          <div className="create-person-modal__reader-links" aria-label="Демо-ссылки чтеца">
            {readerLinks.map((item, index) => (
              <div key={index} className="create-person-modal__reader-link-row">
                <Input
                  inputClassName="create-person-modal__reader-link-name"
                  type="text"
                  value={item.link_name}
                  onChange={(e) => {
                    const value = e.target.value;
                    setReaderLinks((prev) => prev.map((row, i) => (i === index ? { ...row, link_name: value } : row)));
                  }}
                  disabled={saving}
                  placeholder="Название"
                  autoComplete="off"
                  fullWidth
                />
                <div
                  className={`create-person-modal__reader-link-url-field ${!saving ? 'create-person-modal__reader-link-url-field_has-clear' : ''}`.trim()}
                >
                  <input
                    className="create-person-modal__reader-link-url-input"
                    type="url"
                    value={item.link}
                    onChange={(e) => {
                      const value = e.target.value;
                      setReaderLinks((prev) => prev.map((row, i) => (i === index ? { ...row, link: value } : row)));
                    }}
                    disabled={saving}
                    placeholder="Ссылка на демо"
                    autoComplete="off"
                  />
                  {!saving ? (
                    <ComboClearButton
                      aria-label="Удалить демо-ссылку"
                      onClick={() => setReaderLinks((prev) => prev.filter((_, i) => i !== index))}
                    />
                  ) : null}
                </div>
              </div>
            ))}
            <Button
              type="button"
              size="m"
              variant="primary"
              fullWidth
              className="create-person-modal__reader-link-add"
              title={
                readerLinks.length >= MAX_READER_DEMO_LINKS
                  ? `Не больше ${MAX_READER_DEMO_LINKS} демо-ссылок у чтеца`
                  : undefined
              }
              onClick={() =>
                setReaderLinks((prev) =>
                  prev.length >= MAX_READER_DEMO_LINKS ? prev : [...prev, { link: '', link_name: '' }]
                )
              }
              disabled={saving || readerLinks.length >= MAX_READER_DEMO_LINKS}
            >
              Добавить ссылку на демо
            </Button>
          </div>
        </>
      ) : null}

      {isEmployee ? (
        <div className="form__field form__field_full">
          <SelectDropdown
            options={STAFF_ROLE_OPTIONS}
            value={staffRole}
            onChange={(v) => setStaffRole((v || EMPTY_STAFF) as StaffRole)}
            placeholder="Должность сотрудника"
            fullWidth
            disabled={saving || editorRoleLocked}
          />
        </div>
      ) : null}

      {isEmployee && staffRole === 'editor' ? (
        <div className="form__field form__field_full">
          {editionsQuery.isLoading ? (
            <p className="create-person-modal__hint">Загрузка редакций…</p>
          ) : editionsQuery.isError ? (
            <p className="form__error" role="alert">
              Не удалось загрузить редакции
            </p>
          ) : editionOptions.length === 0 ? (
            <p className="create-person-modal__hint" role="status">
              В справочнике нет редакций. Добавьте редакцию в настройках, затем повторите.
            </p>
          ) : (
            <SelectDropdown
              options={editionOptions}
              value={editionId}
              onChange={setEditionId}
              placeholder="Редакция"
              fullWidth
              disabled={saving}
            />
          )}
        </div>
      ) : null}

      {isEmployee && staffRole ? (
        <Switch
          checked={staffDismissed}
          onCheckedChange={setStaffDismissed}
          disabled={saving}
          label="Уволен"
        />
      ) : null}

      {isUser ? (
        <>
          <Switch
            checked={userIsActive}
            onCheckedChange={setUserIsActive}
            disabled={saving}
            label="Активен"
          />
          <div className="create-person-modal__grid">
            <Input
              value={userRole}
              onChange={(e) => setUserRole(e.target.value)}
              disabled={saving}
              fullWidth
              placeholder="Должность"
            />
            <Input
              value={userAccess}
              onChange={(e) => setUserAccess(e.target.value)}
              disabled={saving}
              fullWidth
              placeholder="Доступ"
            />
          </div>
        </>
      ) : null}
    </>
  );

  const formOrWizard =
    wizardStep === 'name' ? (
      <>
        <div className="create-person-modal__grid">
          <Input
            placeholder="Имя"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={checking}
            fullWidth
            autoComplete="given-name"
          />
          <Input
            placeholder="Фамилия"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={checking}
            fullWidth
            autoComplete="family-name"
          />
        </div>
        <div className="create-person-modal__actions create-person-modal__actions--single">
          <Button
            fullWidth
            variant="primary"
            type="button"
            onClick={() => void handleCheckPerson()}
            disabled={checking}
          >
            {checking ? 'Проверка…' : 'Проверить'}
          </Button>
        </div>
      </>
    ) : (
      <>
        {personBootstrapLoading ? (
          <p className="create-person-modal__hint" aria-busy="true">
            Загрузка данных персоны…
          </p>
        ) : (
          formBody
        )}
        <div className="create-person-modal__actions">
          {existingPersonId ? (
            embedded && onRequestCreate ? (
              <Button
                fullWidth
                variant="outlined"
                type="button"
                onClick={onRequestCreate}
                disabled={saving || deletePersonSubmitting || personBootstrapLoading}
              >
                Создать новую
              </Button>
            ) : (
              <Button
                fullWidth
                variant="dangerOutline"
                type="button"
                onClick={() => void openDeletePersonDialog()}
                disabled={saving || deletePersonSubmitting || personBootstrapLoading}
              >
                Удалить персону
              </Button>
            )
          ) : (
            <Button
              fullWidth
              variant="outlined"
              type="button"
              onClick={() => {
                if (embedded) {
                  resetForm();
                  setWizardStep('name');
                  setExistingPersonId(null);
                  setRoleBaseline(null);
                  setSnapshotForDelete(null);
                  return;
                }
                handleClose();
              }}
              disabled={saving || personBootstrapLoading}
            >
              Отмена
            </Button>
          )}
          <Button
            fullWidth
            variant="primary"
            type="button"
            onClick={() => void trySave()}
            disabled={saving || personBootstrapLoading}
          >
            {saving ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </div>
        {existingPersonId && embedded && onRequestCreate ? (
          <Button
            fullWidth
            variant="dangerOutline"
            type="button"
            onClick={() => void openDeletePersonDialog()}
            disabled={saving || deletePersonSubmitting || personBootstrapLoading}
          >
            Удалить персону
          </Button>
        ) : null}
      </>
    );

  return (
    <>
      {embedded ? (
        <div className="create-person-modal__embedded">{formOrWizard}</div>
      ) : (
        <Modal
          open={open}
          onClose={handleClose}
          title="Управление персонами"
          size="md"
          headerVariant="island"
          className="create-person-modal"
          overlayClassName="create-person-modal-overlay"
        >
          <div className="create-person-modal__inner">{formOrWizard}</div>
        </Modal>
      )}

      <ChoiceDialog
        open={removeRolesDialog !== null}
        onClose={() => {
          if (!saving) setRemoveRolesDialog(null);
        }}
        title="Снятие ролей"
        description={removeRolesDescription}
        actions={[
          {
            label: 'Отмена',
            variant: 'outlined',
            onClick: () => setRemoveRolesDialog(null),
            disabled: saving,
          },
          {
            label: 'Сохранить',
            variant: 'dangerOutline',
            onClick: () => void confirmSaveAfterRemoval(),
            disabled: saving,
          },
        ]}
      />

      <ChoiceDialog
        open={deletePersonDialog !== null}
        onClose={() => {
          if (!deletePersonSubmitting) setDeletePersonDialog(null);
        }}
        title="Удаление"
        description={deletePersonDescription}
        actions={[
          {
            label: 'Удалить из базы',
            variant: 'dangerOutline',
            disabled: deletePersonSubmitting,
            onClick: () => void runDeletePerson(),
          },
        ]}
      />
    </>
  );
}
